import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import WidgetFooter from '../../components/WidgetFooter';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { useClientSavedAddresses } from '../../hooks/useClientSavedAddresses';
import { toast } from 'sonner';
import { 
  Leaf, ChevronRight, ChevronLeft, Check, User, Briefcase, 
  Calendar, Calculator, Send, Loader2, Info, MapPin, Mail,
  Phone, Clock, Euro, ExternalLink, CalendarDays, Plus, X, Repeat, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar as CalendarComp } from '../../components/ui/calendar';
import { widgetApi } from '../../lib/api';
import { getImageUrl } from '../../lib/imageUtils';
import { useEmbedAutoresize } from '../../lib/widgetEmbed';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isAfter, isBefore, startOfWeek, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

const STEPS = [
  { id: 'services', title: 'Prestations', icon: Briefcase },
  { id: 'recurrence', title: 'Fréquence', icon: Repeat },
  { id: 'planning', title: 'Planning', icon: Calendar },
  { id: 'contact', title: 'Coordonnées', icon: User },
  { id: 'summary', title: 'Récapitulatif', icon: Calculator },
];

// Duration options - will be dynamically calculated based on work hours
const DEFAULT_DURATION_OPTIONS = [
  { value: 'half_day', label: '½ journée', hours: 3.5 },
  { value: 'full_day', label: 'Journée complète', hours: 7 },
];

// Recurrence options
const RECURRENCE_OPTIONS = [
  { value: 'once', label: '1 intervention', description: 'Prestation ponctuelle unique' },
  { value: 'weekly', label: 'Chaque semaine', description: '4 passages/mois' },
  { value: 'biweekly', label: 'Toutes les 2 semaines', description: '2 passages/mois' },
  { value: 'monthly', label: '1 fois par mois', description: '1 passage/mois' },
  { value: 'custom', label: 'Personnalisé', description: 'Je choisis mes dates librement' },
  { value: 'ask_advice', label: 'Demander conseil', description: 'L\'entreprise me proposera des créneaux' },
];

// Preferred day options
const DAY_OPTIONS = [
  { value: 'monday', label: 'Lundi' },
  { value: 'tuesday', label: 'Mardi' },
  { value: 'wednesday', label: 'Mercredi' },
  { value: 'thursday', label: 'Jeudi' },
  { value: 'friday', label: 'Vendredi' },
  { value: 'saturday', label: 'Samedi' },
  { value: 'flexible', label: 'Flexible (selon disponibilités)' },
];

export default function RecurringWidget() {
  const { widgetId } = useParams();
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('embed') === 'true';
  useEmbedAutoresize(isEmbedded);
  const { addresses: savedAddresses } = useClientSavedAddresses();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // 'not_found' | 'network' | null
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [config, setConfig] = useState(null);
  const [widgetConfig, setWidgetConfig] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [availability, setAvailability] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]); // Créneaux déjà réservés
  const [closures, setClosures] = useState([]); // Fermetures + jours fériés
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Custom theme from widget config or URL params
  const primaryColor = widgetConfig?.primary_color || searchParams.get('primary') || '#8BC34A';
  const secondaryColor = widgetConfig?.secondary_color || searchParams.get('secondary') || '#4CAF50';
  const textColor = widgetConfig?.text_color || searchParams.get('text') || '#1a1a1a';
  const bgColor = widgetConfig?.bg_color || '#F9F8F6';

  // Form data
  const [formData, setFormData] = useState({
    selected_services: [],
    other_service_description: '',
    needs_description: '',
    slot_duration: 'half_day',
    // Recurrence fields
    recurrence_type: 'biweekly',
    preferred_day: 'flexible',
    preferred_period: 'flexible', // morning, afternoon, flexible (UI removed - always flexible)
    start_date: null,
    end_date: null, // Date de fin de période
    date_selection_mode: 'standard', // standard, custom, ask_advice
    // Legacy fields for calendar selection
    selected_slots: [],
    // Selected dates for the subscription period
    selected_dates: [],
    client_name: searchParams.get('prefill_name') || '',
    client_email: searchParams.get('prefill_email') || '',
    client_phone: searchParams.get('prefill_phone') || '',
    client_address: searchParams.get('prefill_address') || '',
    client_coords: null,
    urssaf_email: '',
    notes: '',
    // Collaborator preference
    preferred_collaborator_id: '',
  });

  // Get services and collaborators from config
  const companyServices = config?.services || [];
  const companyCollaborators = config?.collaborators || [];
  const workHours = config?.work_hours || [];
  // Per-collaborator unavailable dates (leaves + busy days). Used to grey out
  // an unavailable preferred collaborator once the user picks dates.
  const collabUnavailableDates = config?.collab_unavailable_dates || {};
  const slotTypes = config?.slot_types || [
    { code: 'half_day', name: 'Demi-journée', duration_hours: 4, required_collaborators: 1 },
    { code: 'full_day', name: 'Journée complète', duration_hours: 7, required_collaborators: 1 }
  ];

  // Calculate dynamic time slots based on work hours
  const getWorkingDays = () => {
    // Return days where company is open (day_of_week: 0=Monday, 1=Tuesday, etc.)
    const activeDays = workHours.filter(wh => wh.is_active);
    return activeDays.map(wh => ({
      dayOfWeek: wh.day_of_week,
      dayName: DAY_OPTIONS.find(d => d.value === getDayValueFromIndex(wh.day_of_week))?.label || '',
      startTime: wh.start_time,
      endTime: wh.end_time
    }));
  };

  // Convert day index to day value
  const getDayValueFromIndex = (index) => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    return days[index] || 'monday';
  };

  // Get available day options based on work hours
  const getAvailableDays = () => {
    const workingDays = getWorkingDays();
    if (workingDays.length === 0) return DAY_OPTIONS; // Fallback to all days
    
    // Use a Set to avoid duplicate days
    const seenDays = new Set();
    const availableDays = [];
    
    workingDays.forEach(wd => {
      const dayValue = getDayValueFromIndex(wd.dayOfWeek);
      if (!seenDays.has(dayValue)) {
        seenDays.add(dayValue);
        availableDays.push({
          value: dayValue,
          label: wd.dayName || DAY_OPTIONS.find(d => d.value === dayValue)?.label
        });
      }
    });
    
    // Always add flexible option if not already present
    if (!seenDays.has('flexible')) {
      availableDays.push({ value: 'flexible', label: 'Flexible (selon disponibilités)' });
    }
    
    return availableDays;
  };

  // Get time slots based on work hours
  const getTimeSlots = () => {
    // Find the earliest and latest work hours across all days
    const activeDays = workHours.filter(wh => wh.is_active);
    
    if (activeDays.length === 0) {
      // Fallback to default hours
      return {
        morning: { start: '08:00', end: '12:00', label: 'Matin', subLabel: '8h - 12h' },
        afternoon: { start: '14:00', end: '18:00', label: 'Après-midi', subLabel: '14h - 18h' }
      };
    }

    // Get the most common/earliest start time and latest end time
    const startTimes = activeDays.map(d => d.start_time).sort();
    const endTimes = activeDays.map(d => d.end_time).sort();
    
    const earliestStart = startTimes[0] || '08:00';
    const latestEnd = endTimes[endTimes.length - 1] || '18:00';
    
    // Parse hours
    const startHour = parseInt(earliestStart.split(':')[0]);
    const startMin = parseInt(earliestStart.split(':')[1]) || 0;
    const endHour = parseInt(latestEnd.split(':')[0]);
    const endMin = parseInt(latestEnd.split(':')[1]) || 0;
    
    // Format time for display
    const formatTime = (time) => {
      const [h, m] = time.split(':');
      return m === '00' ? `${parseInt(h)}h` : `${parseInt(h)}h${m}`;
    };

    // Calculate morning end and afternoon start based on work duration
    // Morning ends at 12h or mid-point if end time is earlier
    const totalHours = endHour - startHour;
    
    // If short day (less than 6h), only show "journée" option
    if (totalHours <= 5) {
      return {
        morning: { 
          start: earliestStart, 
          end: latestEnd, 
          label: 'Journée', 
          subLabel: `${formatTime(earliestStart)} - ${formatTime(latestEnd)}` 
        },
        afternoon: null // No afternoon option for short days
      };
    }
    
    // Standard calculation: morning until 12h or midpoint, afternoon from 13h/14h
    let morningEndHour = Math.min(12, startHour + Math.floor(totalHours / 2));
    let afternoonStartHour = Math.max(13, morningEndHour + 1);
    
    // Ensure afternoon start doesn't exceed end time
    if (afternoonStartHour >= endHour) {
      afternoonStartHour = morningEndHour + 1;
    }
    
    const morningEnd = `${morningEndHour}:00`;
    const afternoonStart = `${afternoonStartHour}:00`;

    return {
      morning: { 
        start: earliestStart, 
        end: morningEnd, 
        label: 'Matin', 
        subLabel: `${formatTime(earliestStart)} - ${formatTime(morningEnd)}` 
      },
      afternoon: { 
        start: afternoonStart, 
        end: latestEnd, 
        label: 'Après-midi', 
        subLabel: `${formatTime(afternoonStart)} - ${formatTime(latestEnd)}` 
      }
    };
  };

  const timeSlots = getTimeSlots();
  const availableDays = getAvailableDays();

  // Calculate dynamic duration options based on actual work hours
  const getDurationOptions = () => {
    const activeDays = workHours.filter(wh => wh.is_active);
    
    if (activeDays.length === 0) {
      return DEFAULT_DURATION_OPTIONS;
    }

    // Get average work hours per day
    const totalHours = activeDays.reduce((sum, day) => {
      const start = day.start_time.split(':');
      const end = day.end_time.split(':');
      const startMinutes = parseInt(start[0]) * 60 + (parseInt(start[1]) || 0);
      const endMinutes = parseInt(end[0]) * 60 + (parseInt(end[1]) || 0);
      const hoursWorked = (endMinutes - startMinutes) / 60;
      // Subtract 1 hour for lunch break if working more than 6 hours
      return sum + (hoursWorked > 6 ? hoursWorked - 1 : hoursWorked);
    }, 0);
    
    const avgHoursPerDay = totalHours / activeDays.length;
    const halfDayHours = Math.round((avgHoursPerDay / 2) * 10) / 10; // Round to 1 decimal
    const fullDayHours = Math.round(avgHoursPerDay * 10) / 10;

    // Use slot types from config if available
    if (slotTypes.length > 0) {
      return slotTypes.map(st => ({
        value: st.code || st.id,
        id: st.id,
        code: st.code,
        label: `${st.name} ${st.duration_hours}H`,
        hours: st.duration_hours,
        required_collaborators: st.required_collaborators || 1
      }));
    }

    return [
      { value: 'half_day', label: `Demi-journée ${halfDayHours}H`, hours: halfDayHours },
      { value: 'full_day', label: `Journée complète ${fullDayHours}H`, hours: fullDayHours },
    ];
  };

  const DURATION_OPTIONS = getDurationOptions();

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await widgetApi.getRecurringConfig(widgetId);
        setConfig(response.data);
        setWidgetConfig(response.data.widget);
        setAvailability(response.data.availability || []);
        setBookedSlots(response.data.booked_slots || []); // Store booked slots
        setClosures(response.data.closures || []); // Fermetures + jours fériés
      } catch (error) {
        console.error('Error loading widget config:', error);
        const status = error?.response?.status;
        setLoadError(status === 404 ? 'not_found' : 'network');
      } finally {
        setIsLoading(false);
      }
    };
    fetchConfig();
  }, [widgetId]);

  // When the widget exposes a single service, pre-select it so the user can
  // simply hit "Suivant" without clicking. We deliberately do NOT auto-jump
  // to step 1 here: on the recurring widget, the first step also contains
  // important context (eligible services, "autre" option, descriptions),
  // and skipping it disorientates new clients.
  useEffect(() => {
    if (!config || !companyServices.length) return;
    if (companyServices.length !== 1) return;
    const onlyId = companyServices[0].id;
    if (formData.selected_services.length === 0) {
      setFormData((prev) => ({ ...prev, selected_services: [onlyId] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // Check if a slot is already booked.
  // Collab-aware semantics:
  //  • When a preferred collaborator is selected → the slot is "booked" only
  //    if THAT collaborator is busy (other collabs being busy is irrelevant).
  //  • Without a preferred collaborator → the slot is "fully booked" only if
  //    ALL widget-eligible collaborators are busy.
  // This fixes the production bug where a slot showed as taken even when
  // other intervenants were still available, AND vice-versa.
  const isSlotBooked = (date, period) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const matchingSlots = bookedSlots.filter(slot => {
      if (slot.date !== dateStr) return false;
      if (slot.period === 'full_day') return true;
      return slot.period === period;
    });

    const preferredId = formData.preferred_collaborator_id;
    if (preferredId) {
      // Mark as booked if the preferred collab has a matching booking OR is
      // on leave that day (collab_unavailable_dates includes leaves).
      const onLeave = (collabUnavailableDates[preferredId] || []).includes(dateStr);
      if (onLeave) return true;
      return matchingSlots.some(s => (s.collaborator_ids || []).includes(preferredId));
    }

    // No preference: only mark as booked when every eligible collaborator is
    // busy. Eligibles = collabs returned in the widget config (already
    // filtered by widget.assigned_collaborator_ids + assigned_services).
    const eligibleIds = (companyCollaborators || []).map(c => c.id);
    if (eligibleIds.length === 0) {
      // Legacy widgets without configured collabs → only booked when a
      // booking actually exists for that period.
      return matchingSlots.length > 0;
    }
    const busyIds = new Set();
    for (const s of matchingSlots) {
      for (const cid of (s.collaborator_ids || [])) busyIds.add(cid);
    }
    // A collab is unavailable for this date if they're either busy on a
    // matching slot OR on an approved leave (collab_unavailable_dates already
    // aggregates leaves + full-day busy entries).
    const isUnavailable = (cid) => {
      if (busyIds.has(cid)) return true;
      const unavail = collabUnavailableDates[cid] || [];
      return unavail.includes(dateStr);
    };
    // If at least one eligible collab is free → slot is NOT fully booked.
    return eligibleIds.every(isUnavailable);
  };

  // Calculate remaining dates to select
  const getRemainingDatesToSelect = () => {
    const expectedDates = calculateExpectedDates();
    // When the client picks full-day slots, `selected_slots` stores 2 entries
    // per date (morning + afternoon). Count each unique date only once.
    let selectedCount;
    if (formData.selected_dates?.length) {
      selectedCount = formData.selected_dates.length;
    } else if (formData.slot_duration === 'full_day' && formData.selected_slots?.length) {
      const days = new Set(formData.selected_slots.map(s => new Date(s.date).toISOString().slice(0, 10)));
      selectedCount = days.size;
    } else {
      selectedCount = formData.selected_slots?.length || 0;
    }
    return Math.max(0, expectedDates - selectedCount);
  };

  const hourlyRate = config?.hourly_rate || config?.company?.default_hourly_rate || 35;
  const hoursPerSlot = formData.slot_duration === 'full_day' ? 7 : 3.5;

  // Pricing mode coming from the widget configuration (back-office editor).
  // Mirrors PunctualWidget so the recurring widget honours per_slot_type pricing.
  const widgetPricingMode = config?.widget?.pricing_mode || 'hourly';
  const widgetSlotTypePrices = config?.widget?.slot_type_prices || {};

  // Resolve the price for a duration option (= a slot type) according to
  // the widget pricing mode. Falls back to hourly × hours when no fixed
  // price is configured or pricing mode is hourly.
  const getDurationOptionPrice = (opt) => {
    if (widgetPricingMode === 'per_slot_type') {
      const fixed = widgetSlotTypePrices[opt.id] ?? widgetSlotTypePrices[opt.code] ?? widgetSlotTypePrices[opt.value];
      if (fixed != null && fixed > 0) return fixed;
    }
    const collab = opt.required_collaborators || 1;
    return (opt.hours || 0) * hourlyRate * collab;
  };

  // Selected slot duration is a full-day-like type? If yes, the period
  // selector (matin/aprem/flexible) should be hidden because the booking
  // takes the entire working day.
  const currentSlotOption = (() => {
    // DURATION_OPTIONS is computed later in render — replicate the same logic
    // by looking up the slot type matching the current selection.
    const st = slotTypes.find(s => (s.code || s.id) === formData.slot_duration);
    return st || null;
  })();
  const _isRecurringFullDay = (() => {
    if (!currentSlotOption) {
      return formData.slot_duration === 'full_day';
    }
    const code = (currentSlotOption.code || '').toLowerCase();
    const name = (currentSlotOption.name || '').toLowerCase();
    // A "demi-journée" name contains "journée" too — bail out early so a
    // half-day slot is never mis-detected as a full-day one (which would
    // hide the AM/PM picker and force the client to book the whole day).
    if (/(^|[^a-z])demi([^a-z]|$)|half|matin|apr[eè]s|aprem/.test(code)
        || /demi|half|matin|apr[eè]s|aprem/.test(name)) {
      return false;
    }
    return (
      code === 'full_day' ||
      /journee|complete|full/.test(code) ||
      /journ[eé]e|complete|full/.test(name) ||
      (currentSlotOption.duration_hours && currentSlotOption.duration_hours >= 6)
    );
  })();

  // Get the price of the currently selected slot duration (1 visit).
  const getSelectedSlotPrice = () => {
    const opt = DURATION_OPTIONS.find(o => o.value === formData.slot_duration);
    if (!opt) return hoursPerSlot * hourlyRate;
    return getDurationOptionPrice(opt);
  };

  // Get monthly slots based on recurrence type
  const getMonthlySlots = () => {
    switch (formData.recurrence_type) {
      case 'weekly': return 4;
      case 'biweekly': return 2;
      case 'monthly': return 1;
      case 'custom': return formData.selected_slots.length || 1;
      default: return 2;
    }
  };

  // ─── Avance immédiate crédit d'impôt (50% URSSAF) ───
  // True when every selected service is flagged tax_credit_eligible — same
  // logic as PunctualWidget to keep the customer-facing message consistent.
  const _isTaxCreditEligible = (() => {
    const ids = formData.selected_services || [];
    if (ids.length === 0) return false;
    const list = companyServices.filter((s) => ids.includes(s.id));
    if (list.length === 0) return false;
    return list.every((s) => s.tax_credit_eligible !== false);
  })();
  const TaxCreditNote = ({ price, align = 'right' }) => {
    if (!_isTaxCreditEligible || !price || price <= 0) return null;
    const remaining = price * 0.5;
    return (
      <div className={`${align === 'right' ? 'text-right' : ''} mt-1`} data-testid="tax-credit-note">
        <p className="text-sm font-medium" style={{ color: primaryColor }}>
          Reste à votre charge : {remaining.toFixed(2)}€
        </p>
        <p className="text-xs italic text-gray-500">
          (Après déduction d'avance immédiate de crédit d'impôts)
        </p>
      </div>
    );
  };

  // Calculate pricing
  const calculatePricing = () => {
    // Determine the actual number of unique slots: for full-day picks, each
    // date is a single slot even though selected_slots stores morning+afternoon.
    const uniqueSlotCount = (() => {
      if (formData.slot_duration !== 'full_day') return formData.selected_slots.length;
      const days = new Set(formData.selected_slots.map(s => new Date(s.date).toISOString().slice(0, 10)));
      return days.size;
    })();
    const numSlots = formData.recurrence_type === 'custom' 
      ? Math.max(uniqueSlotCount, 1)
      : getMonthlySlots();
    
    const pricePerVisit = getSelectedSlotPrice();
    const totalPrice = pricePerVisit * numSlots;
    const frequency = RECURRENCE_OPTIONS.find(r => r.value === formData.recurrence_type)?.label || 'personnalisé';
    const urssafPrice = totalPrice / 2; // 50% tax credit

    return {
      perVisit: pricePerVisit,
      total: totalPrice,
      frequency,
      urssafTotal: urssafPrice,
      numSlots,
      hoursPerSlot
    };
  };

  // Calculate expected number of dates based on frequency and period
  const calculateExpectedDates = () => {
    if (formData.recurrence_type === 'once') return 1; // Single intervention
    if (!formData.start_date || !formData.end_date) return 0;
    
    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.ceil(diffDays / 7);
    
    switch (formData.recurrence_type) {
      case 'weekly':
        return diffWeeks;
      case 'biweekly':
        return Math.ceil(diffWeeks / 2);
      case 'monthly':
        return Math.ceil(diffDays / 30);
      default:
        return 0;
    }
  };

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      selected_services: prev.selected_services.includes(serviceId)
        ? prev.selected_services.filter(s => s !== serviceId)
        : [...prev.selected_services, serviceId]
    }));
  };

  const handleSlotToggle = (date, period) => {
    setFormData(prev => {
      const existingSlot = prev.selected_slots.find(
        s => isSameDay(new Date(s.date), date) && s.period === period
      );
      if (existingSlot) {
        return {
          ...prev,
          selected_slots: prev.selected_slots.filter(
            s => !(isSameDay(new Date(s.date), date) && s.period === period)
          )
        };
      }
      return {
        ...prev,
        selected_slots: [...prev.selected_slots, { date: date.toISOString(), period }]
      };
    });
  };

  const isSlotSelected = (date, period) => {
    return formData.selected_slots.some(
      s => isSameDay(new Date(s.date), date) && s.period === period
    );
  };

  // Returns the list of selected dates that the preferred collaborator
  // (when set) cannot honour. Used both to render the inline warning and to
  // block "Suivant" from the planning step.
  const conflictingPreferredCollabDates = () => {
    if (!formData.preferred_collaborator_id) return [];
    const unavailable = new Set(collabUnavailableDates[formData.preferred_collaborator_id] || []);
    if (unavailable.size === 0) return [];
    const dates = formData.selected_slots
      .map(s => new Date(s.date).toISOString().split('T')[0])
      .filter(d => unavailable.has(d));
    return Array.from(new Set(dates));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: // Services
        return formData.selected_services.length > 0;
      case 1: // Recurrence
        if (formData.recurrence_type === 'ask_advice') {
          return true; // Can proceed without selecting dates/period
        }
        if (formData.recurrence_type === 'custom') {
          return true; // Will select dates in planning step
        }
        if (formData.recurrence_type === 'once') {
          return true; // Single intervention - just need to select a date
        }
        // Standard frequency: need period selected
        return formData.recurrence_type && formData.start_date && formData.end_date;
      case 2: // Planning (optional - can skip if recurrence is set)
        // Block "Suivant" if the preferred collab is on leave / busy on one
        // of the picked dates — server would refuse anyway with a 409.
        if (conflictingPreferredCollabDates().length > 0) return false;
        return true;
      case 3: // Contact
        return formData.client_name && formData.client_email && formData.client_phone && formData.client_address;
      default:
        return true;
    }
  };

  // Handle next step - skip planning if "ask_advice"
  const handleNext = () => {
    if (currentStep === 1 && formData.recurrence_type === 'ask_advice') {
      // Skip planning step, go directly to contact
      setCurrentStep(3);
    } else {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
    }
  };

  // Handle previous step - account for skipped planning
  const handlePrevious = () => {
    if (currentStep === 3 && formData.recurrence_type === 'ask_advice') {
      // If we skipped planning, go back to recurrence
      setCurrentStep(1);
    } else {
      setCurrentStep(prev => Math.max(prev - 1, 0));
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const pricing = calculatePricing();
      const companyId = config?.company?.id;
      
      // Get selected service names for display
      const selectedServiceNames = formData.selected_services.map(id => {
        const service = companyServices.find(s => s.id === id);
        return service?.name || id;
      });
      
      // Determine status based on selection mode
      let initialStatus = 'pending';
      if (formData.recurrence_type === 'ask_advice') {
        initialStatus = 'advice_requested';
      } else if (!formData.selected_slots.length && formData.recurrence_type !== 'custom') {
        initialStatus = 'awaiting_dates';
      }
      
      await widgetApi.subscribe(companyId, {
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_phone: formData.client_phone,
        client_address: formData.client_address,
        client_coords: formData.client_coords || null,
        urssaf_email: formData.urssaf_email,
        selected_services: formData.selected_services,
        selected_service_names: selectedServiceNames,
        other_service_description: formData.other_service_description,
        slot_duration: formData.slot_duration,
        // Collaborator preference
        preferred_collaborator_id: formData.preferred_collaborator_id || null,
        // Recurrence data
        recurrence_type: formData.recurrence_type,
        preferred_day: formData.preferred_day,
        preferred_period: formData.preferred_period,
        start_date: formData.start_date,
        end_date: formData.end_date,
        date_selection_mode: formData.date_selection_mode,
        selected_dates: formData.selected_dates,
        // Legacy — collapse morning+afternoon pairs into a single full_day
        // entry when the client picked full days, so 3 days = 3 slots
        // (not 6). Half-day picks keep their existing shape.
        selected_slots: (() => {
          if (formData.slot_duration !== 'full_day') return formData.selected_slots;
          const byDate = new Map();
          for (const s of (formData.selected_slots || [])) {
            const key = new Date(s.date).toISOString().slice(0, 10);
            const prev = byDate.get(key);
            byDate.set(key, prev
              ? { date: s.date, period: 'full_day' }
              : { date: s.date, period: s.period }
            );
          }
          return Array.from(byDate.values()).map(v => ({ date: v.date, period: 'full_day' }));
        })(),
        needs_description: formData.needs_description,
        notes: formData.notes,
        estimated_monthly: pricing.perVisit,
        estimated_monthly_urssaf: pricing.urssafTotal / Math.max(pricing.numSlots, 1),
        estimated_annual: pricing.total * 12 / Math.max(pricing.numSlots, 1),
        initial_status: initialStatus
      });

      setSubmitted(true);
      toast.success('Votre demande a été envoyée avec succès !');
    } catch (error) {
      console.error('Submission error:', error);
      const detail = error?.response?.data?.detail;
      let message = 'Une erreur est survenue. Veuillez réessayer.';
      if (typeof detail === 'string' && detail) {
        message = detail;
      } else if (detail && typeof detail === 'object') {
        if (Array.isArray(detail.conflicts)) {
          const dates = detail.conflicts.map((c) => c.date).join(', ');
          message = `${detail.message || 'Conflit de planning'} : ${dates}`;
        } else if (detail.message) {
          message = detail.message;
        } else if (Array.isArray(detail) && detail[0]?.msg) {
          // FastAPI validation errors (422) come as an array of issues
          message = detail.map((e) => e.msg).join(' • ');
        }
      } else if (error?.response?.status >= 500) {
        message = 'Erreur serveur, merci de réessayer dans un instant.';
      }
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calendar helpers
  const getDaysInMonth = () => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  };

  // Detect whether a given date falls inside a company closure or French
  // national holiday — these are returned by the backend in `closures`.
  const getClosureReason = (date) => {
    if (!closures || closures.length === 0) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const iso = `${y}-${m}-${d}`;
    const hit = closures.find((c) => {
      const start = c.start_date;
      const end = c.end_date || start;
      if (!start) return false;
      return iso >= start && iso <= end;
    });
    if (!hit) return null;
    if (hit.type === 'french_holiday') return hit.reason || 'Jour férié';
    return hit.reason || 'Fermeture';
  };
  const isDateClosed = (date) => getClosureReason(date) !== null;

  const isDateAvailable = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check if date is in the future
    if (!isAfter(date, today) && !isSameDay(date, today)) {
      return false;
    }

    // Block company closures + French national holidays
    if (isDateClosed(date)) {
      return false;
    }

    // Check if day of week is a working day
    const dayOfWeek = date.getDay();
    // Convert JS day (0=Sunday) to our format (0=Monday)
    const normalizedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    
    // Get active work hours for this day
    const activeDays = workHours.filter(wh => wh.is_active).map(wh => wh.day_of_week);
    
    return activeDays.includes(normalizedDay);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: bgColor }}>
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: primaryColor }} />
          <p className="mt-4 text-gray-600">Chargement du formulaire...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={isEmbedded ? "py-10 px-4" : "min-h-screen flex items-center justify-center p-4"} style={{ backgroundColor: isEmbedded ? 'transparent' : bgColor }}>
        <Card className="max-w-md w-full mx-auto" data-testid="widget-load-error">
          <CardContent className="pt-6 text-center">
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-7 h-7 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2 text-slate-900">
              {loadError === 'not_found' ? 'Service indisponible' : 'Erreur de connexion'}
            </h2>
            <p className="text-gray-600 text-sm">
              {loadError === 'not_found'
                ? "Ce widget d'abonnement n'est pas accessible pour le moment. Merci de contacter le prestataire."
                : "Impossible de joindre le serveur. Réessayez dans quelques instants."}
            </p>
            {loadError === 'network' && (
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white"
                style={{ backgroundColor: primaryColor }}
              >
                Réessayer
              </button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: bgColor }}>
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${primaryColor}20` }}>
              <Check className="w-8 h-8" style={{ color: primaryColor }} />
            </div>
            <h2 className="text-2xl font-bold mb-2">{widgetConfig?.confirmation_title || 'Demande envoyée !'}</h2>
            <p className="text-gray-600">
              {widgetConfig?.confirmation_message || `Merci ${formData.client_name} ! Votre demande d'abonnement a été transmise à ${config?.company?.name}. Vous recevrez une confirmation par email.`}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pricing = calculatePricing();

  return (
    <div
      className={isEmbedded ? "py-4 px-4" : "min-h-screen py-8 px-4"}
      style={{ backgroundColor: isEmbedded ? 'transparent' : bgColor }}
    >
      <div className="max-w-2xl mx-auto">
        {/* Header with company info — hidden when embedded inside client space */}
        {!isEmbedded && (
          <div className="text-center mb-8">
            {config?.company?.logo_url && widgetConfig?.show_logo !== false && (
              <div
                className="w-32 h-32 mx-auto mb-4 rounded-full bg-white flex items-center justify-center overflow-hidden p-3 border-2 transition-all duration-300 hover:scale-105"
                style={{ borderColor: primaryColor, boxShadow: `0 4px 24px ${primaryColor}33` }}
              >
                <img 
                  src={getImageUrl(config.company.logo_url)}
                  alt={config?.company?.name}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            )}
            <h1 className="text-2xl font-bold" style={{ color: textColor }}>{widgetConfig?.name || config?.company?.name}</h1>
            <p className="text-gray-600">{widgetConfig?.description || widgetConfig?.intro_text || 'Demande d\'abonnement pour des prestations régulières'}</p>
          </div>
        )}

        {/* Steps indicator */}
        <div className="flex items-center justify-center mb-8 gap-1 sm:gap-2 px-2 overflow-x-auto">
          {STEPS.map((step, idx) => (
            <div key={step.id} className="flex items-center shrink-0">
              <div
                className={`w-7 h-7 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-colors ${
                  idx <= currentStep ? 'text-white' : 'bg-gray-200 text-gray-500'
                }`}
                style={{ backgroundColor: idx <= currentStep ? primaryColor : undefined }}
              >
                {idx < currentStep ? <Check className="w-3.5 h-3.5 sm:w-5 sm:h-5" /> : <step.icon className="w-3.5 h-3.5 sm:w-5 sm:h-5" />}
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`w-4 sm:w-12 h-1 mx-0.5 sm:mx-1 ${idx < currentStep ? '' : 'bg-gray-200'}`} 
                     style={{ backgroundColor: idx < currentStep ? primaryColor : undefined }} />
              )}
            </div>
          ))}
        </div>

        {/* Form content */}
        <Card>
          <CardHeader>
            <CardTitle>{STEPS[currentStep].title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 0: Services */}
            {currentStep === 0 && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Sélectionnez les prestations souhaitées</p>
                
                {companyServices.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {companyServices.map(service => (
                      <button
                        key={service.id}
                        onClick={() => handleServiceToggle(service.id)}
                        className={`p-4 rounded-lg border text-left transition-all ${
                          formData.selected_services.includes(service.id)
                            ? 'border-2 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                        }`}
                        style={formData.selected_services.includes(service.id) ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                        data-testid={`service-${service.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                            style={{ backgroundColor: formData.selected_services.includes(service.id) ? primaryColor : '#f3f4f6' }}
                          >
                            {formData.selected_services.includes(service.id) ? (
                              <Check className="w-5 h-5 text-white" />
                            ) : (
                              <Briefcase className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{service.name}</p>
                            {service.description && (
                              <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{service.description}</p>
                            )}
                            {service.price_per_hour && (
                              service.tax_credit_eligible !== false ? (
                                <div className="mt-1.5" data-testid={`service-price-rec-${service.id}`}>
                                  <p className="text-[10px] uppercase tracking-wide font-medium text-gray-500 leading-none">
                                    Reste à charge / heure
                                  </p>
                                  <p className="text-base font-semibold leading-tight mt-0.5" style={{ color: primaryColor }}>
                                    {(service.price_per_hour * 0.5).toFixed(2)}€/h
                                  </p>
                                  <p className="text-[11px] text-gray-500 leading-none mt-0.5">
                                    au lieu de <span className="font-semibold">{service.price_per_hour}€/h</span>
                                  </p>
                                </div>
                              ) : (
                                <p className="text-xs mt-1" style={{ color: primaryColor }}>
                                  {service.price_per_hour}€/h
                                </p>
                              )
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center text-gray-500 bg-gray-50 rounded-lg">
                    <Briefcase className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p>Aucun service configuré</p>
                  </div>
                )}
                
                {/* Option "Autre" */}
                <div className="pt-2">
                  <button
                    onClick={() => handleServiceToggle('autre')}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${
                      formData.selected_services.includes('autre')
                        ? 'border-2'
                        : 'border-dashed border-gray-300 hover:border-gray-400'
                    }`}
                    style={formData.selected_services.includes('autre') ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                  >
                    <span className="text-sm text-gray-600">+ Autre prestation (préciser)</span>
                  </button>
                </div>
                
                {formData.selected_services.includes('autre') && (
                  <Textarea
                    placeholder="Décrivez les autres prestations souhaitées..."
                    value={formData.other_service_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, other_service_description: e.target.value }))}
                  />
                )}
                
                {/* Duration choice - can be hidden via widget config */}
                {!config?.widget?.hide_duration_choice && (
                  <div className="space-y-2 pt-4 border-t">
                    <Label>Durée des interventions</Label>
                    <RadioGroup value={formData.slot_duration} onValueChange={(v) => setFormData(prev => ({ ...prev, slot_duration: v }))}>
                      {DURATION_OPTIONS.map(opt => {
                        const estimatedPrice = getDurationOptionPrice(opt);
                        const isPerSlot = widgetPricingMode === 'per_slot_type';
                        const collab = opt.required_collaborators || 1;
                        return (
                          <div 
                            key={opt.value} 
                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                              formData.slot_duration === opt.value 
                                ? 'border-primary bg-primary/5' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => setFormData(prev => ({ ...prev, slot_duration: opt.value }))}
                          >
                            <div className="flex items-center space-x-3">
                              <RadioGroupItem value={opt.value} id={opt.value} />
                              <div>
                                <Label htmlFor={opt.value} className="font-medium cursor-pointer">{opt.label}</Label>
                                <p className="text-xs text-muted-foreground">
                                  {opt.hours}h d'intervention
                                  {collab > 1 ? ` · ${collab} intervenants` : ''}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              {_isTaxCreditEligible ? (
                                <>
                                  <p className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700 leading-tight">Reste à charge</p>
                                  <span className="font-bold text-2xl text-emerald-600 leading-tight block">
                                    {(estimatedPrice * 0.5).toFixed(0)}€
                                  </span>
                                  <p className="text-[11px] text-muted-foreground line-through leading-tight">
                                    {estimatedPrice.toFixed(0)}€<sup className="text-primary no-underline">*</sup>
                                  </p>
                                  {!isPerSlot && (
                                    <p className="text-[9px] text-muted-foreground leading-tight">
                                      {collab > 1
                                        ? `${hourlyRate}€/h × ${opt.hours}h × ${collab}`
                                        : `${hourlyRate}€/h × ${opt.hours}h`}
                                    </p>
                                  )}
                                </>
                              ) : (
                                <>
                                  <span className="font-semibold text-primary">{estimatedPrice.toFixed(0)}€</span>
                                  {!isPerSlot && (
                                    <p className="text-[10px] text-muted-foreground">
                                      {collab > 1
                                        ? `${hourlyRate}€/h × ${opt.hours}h × ${collab}`
                                        : `${hourlyRate}€/h × ${opt.hours}h`}
                                    </p>
                                  )}
                                  {isPerSlot && (
                                    <p className="text-[10px] text-muted-foreground">Tarif forfaitaire</p>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </RadioGroup>
                    <p className="text-xs text-muted-foreground">
                      {widgetPricingMode === 'per_slot_type'
                        ? 'Tarifs forfaitaires par type de créneau. Le prix final peut varier selon les prestations.'
                        : `Tarif indicatif basé sur ${hourlyRate}€/h. Le prix final peut varier selon les prestations.`}
                    </p>
                    {_isTaxCreditEligible && (
                      <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 leading-relaxed" data-testid="tax-credit-footnote">
                        <span className="font-semibold">*</span> Le reste à charge tient compte de l&apos;<span className="font-semibold">avance immédiate du crédit d&apos;impôt de 50%</span> appliquée directement par l&apos;URSSAF aux services à la personne éligibles. Vous ne payez que la moitié, sans attendre votre déclaration de revenus.
                      </p>
                    )}
                  </div>
                )}
                
                {/* When duration is hidden, show info message */}
                {config?.widget?.hide_duration_choice && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-sm text-blue-700">
                      La durée des interventions sera déterminée par l'entreprise selon vos besoins.
                    </p>
                  </div>
                )}

                {/* Collaborator preference */}
                {companyCollaborators.length > 0 && (
                  <div className="space-y-3 pt-4 border-t">
                    <Label>Intervenant préféré (optionnel)</Label>
                    <Select
                      value={formData.preferred_collaborator_id || "none"}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, preferred_collaborator_id: value === "none" ? "" : value }))}
                    >
                      <SelectTrigger data-testid="collaborator-select">
                        <SelectValue placeholder="Pas de préférence" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Pas de préférence</SelectItem>
                        {companyCollaborators.map((collab) => {
                          // Compute date-availability score for this collab
                          const pickedDates = (formData.selected_dates?.length)
                            ? formData.selected_dates.map(d => (d instanceof Date ? d : new Date(d)).toISOString().slice(0, 10))
                            : Array.from(new Set((formData.selected_slots || []).map(s => new Date(s.date).toISOString().slice(0, 10))));
                          const unavailSet = new Set(collabUnavailableDates[collab.id] || []);
                          const totalPicked = pickedDates.length;
                          const conflicts = pickedDates.filter(d => unavailSet.has(d)).length;
                          const fullyUnavailable = totalPicked > 0 && conflicts === totalPicked;
                          const partiallyUnavailable = totalPicked > 0 && conflicts > 0 && conflicts < totalPicked;
                          return (
                            <SelectItem
                              key={collab.id}
                              value={collab.id}
                              disabled={fullyUnavailable}
                              data-testid={`rec-collab-option-${collab.id}${fullyUnavailable ? '-busy' : ''}`}
                            >
                              <div className={`flex items-center gap-2 ${fullyUnavailable ? 'opacity-50' : ''}`}>
                                {collab.avatar_url ? (
                                  <img
                                    src={getImageUrl(collab.avatar_url)}
                                    alt={collab.name}
                                    className={`w-6 h-6 rounded-full object-cover ${fullyUnavailable ? 'grayscale' : ''}`}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ) : (
                                  <div 
                                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-white ${fullyUnavailable ? 'grayscale' : ''}`}
                                    style={{ backgroundColor: collab.color || primaryColor }}
                                  >
                                    {collab.name?.charAt(0)?.toUpperCase() || '?'}
                                  </div>
                                )}
                                <span className={fullyUnavailable ? 'line-through' : ''}>{collab.name}</span>
                                {fullyUnavailable && (
                                  <Badge variant="secondary" className="ml-1 bg-gray-200 text-gray-600 text-[10px] px-1.5">
                                    Occupé
                                  </Badge>
                                )}
                                {partiallyUnavailable && (
                                  <Badge variant="secondary" className="ml-1 bg-amber-100 text-amber-700 text-[10px] px-1.5">
                                    {conflicts}/{totalPicked} indispo
                                  </Badge>
                                )}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      {(() => {
                        const pickedDates = (formData.selected_dates?.length)
                          ? formData.selected_dates.length
                          : new Set((formData.selected_slots || []).map(s => new Date(s.date).toISOString().slice(0, 10))).size;
                        if (pickedDates === 0) {
                          return `${companyCollaborators.length} intervenant${companyCollaborators.length > 1 ? 's' : ''} disponible${companyCollaborators.length > 1 ? 's' : ''}`;
                        }
                        const fullyAvail = companyCollaborators.filter(c => {
                          const unavailSet = new Set(collabUnavailableDates[c.id] || []);
                          const list = (formData.selected_dates?.length)
                            ? formData.selected_dates.map(d => (d instanceof Date ? d : new Date(d)).toISOString().slice(0, 10))
                            : Array.from(new Set((formData.selected_slots || []).map(s => new Date(s.date).toISOString().slice(0, 10))));
                          return !list.every(d => unavailSet.has(d));
                        }).length;
                        return `${fullyAvail} dispo sur ${companyCollaborators.length} pour vos dates`;
                      })()}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 1: Recurrence */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="space-y-3">
                  <Label className="text-base font-medium">Fréquence des passages</Label>
                  <RadioGroup 
                    value={formData.recurrence_type} 
                    onValueChange={(v) => setFormData(prev => ({ 
                      ...prev, 
                      recurrence_type: v,
                      date_selection_mode: v === 'ask_advice' ? 'ask_advice' : v === 'custom' ? 'custom' : 'standard'
                    }))}
                    className="grid grid-cols-1 gap-3"
                  >
                    {RECURRENCE_OPTIONS.map(opt => (
                      <div 
                        key={opt.value} 
                        className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-all ${
                          formData.recurrence_type === opt.value ? 'border-2' : 'border-gray-200'
                        }`}
                        style={formData.recurrence_type === opt.value ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                        onClick={() => setFormData(prev => ({ 
                          ...prev, 
                          recurrence_type: opt.value,
                          date_selection_mode: opt.value === 'ask_advice' ? 'ask_advice' : opt.value === 'custom' ? 'custom' : 'standard'
                        }))}
                      >
                        <RadioGroupItem value={opt.value} id={`recurrence-${opt.value}`} />
                        <div className="flex-1">
                          <Label htmlFor={`recurrence-${opt.value}`} className="font-medium cursor-pointer">{opt.label}</Label>
                          <p className="text-xs text-gray-500">{opt.description}</p>
                        </div>
                        {formData.recurrence_type === opt.value && (
                          <Check className="w-5 h-5" style={{ color: primaryColor }} />
                        )}
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {/* Période de l'abonnement - sauf si "Demander conseil" ou "once" */}
                {formData.recurrence_type !== 'ask_advice' && formData.recurrence_type !== 'once' && (
                  <div className="space-y-4 border-t pt-4">
                    <Label className="text-base font-medium">Période de l'abonnement</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm">Date de début</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              data-testid="start-date-trigger"
                              className={`w-full justify-start text-left font-normal ${!formData.start_date ? 'text-muted-foreground' : ''}`}
                            >
                              <CalendarDays className="w-4 h-4 mr-2 opacity-70" />
                              {formData.start_date
                                ? format(new Date(formData.start_date), 'dd MMMM yyyy', { locale: fr })
                                : 'Choisir une date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarComp
                              mode="single"
                              locale={fr}
                              selected={formData.start_date ? new Date(formData.start_date) : undefined}
                              onSelect={(d) => d && setFormData(prev => ({
                                ...prev,
                                start_date: format(d, 'yyyy-MM-dd'),
                                // Reset end_date if it becomes invalid
                                end_date: prev.end_date && new Date(prev.end_date) < d ? '' : prev.end_date,
                              }))}
                              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Date de fin</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              data-testid="end-date-trigger"
                              disabled={!formData.start_date}
                              className={`w-full justify-start text-left font-normal ${!formData.end_date ? 'text-muted-foreground' : ''}`}
                            >
                              <CalendarDays className="w-4 h-4 mr-2 opacity-70" />
                              {formData.end_date
                                ? format(new Date(formData.end_date), 'dd MMMM yyyy', { locale: fr })
                                : 'Choisir une date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarComp
                              mode="single"
                              locale={fr}
                              selected={formData.end_date ? new Date(formData.end_date) : undefined}
                              onSelect={(d) => d && setFormData(prev => ({ ...prev, end_date: format(d, 'yyyy-MM-dd') }))}
                              disabled={(d) => {
                                const min = formData.start_date ? new Date(formData.start_date) : new Date(new Date().setHours(0, 0, 0, 0));
                                return d < min;
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    
                    {/* Nombre de dates à sélectionner */}
                    {formData.start_date && formData.end_date && formData.recurrence_type !== 'custom' && (
                      <div className="p-4 rounded-lg" style={{ backgroundColor: `${primaryColor}15` }}>
                        <p className="text-sm font-medium">
                          📅 Environ <span className="text-lg font-bold" style={{ color: primaryColor }}>
                            {calculateExpectedDates()} dates
                          </span> à sélectionner
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Basé sur la fréquence "{RECURRENCE_OPTIONS.find(o => o.value === formData.recurrence_type)?.label}" 
                          du {format(new Date(formData.start_date), 'dd/MM/yyyy')} au {format(new Date(formData.end_date), 'dd/MM/yyyy')}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Message si "1 intervention" (ponctuel) */}
                {formData.recurrence_type === 'once' && (
                  <div className="p-4 rounded-lg bg-orange-50 border border-orange-200">
                    <div className="flex items-start gap-3">
                      <CalendarDays className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-orange-800">Intervention unique</p>
                        <p className="text-xs text-orange-600 mt-1">
                          Vous pourrez choisir la date souhaitée à l'étape suivante.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Message si "Demander conseil" */}
                {formData.recurrence_type === 'ask_advice' && (
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-800">Demande de conseil</p>
                        <p className="text-xs text-blue-600 mt-1">
                          L'entreprise vous proposera des créneaux adaptés à vos besoins. 
                          Vous pourrez les valider depuis votre espace client.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Jour et créneau préféré - sauf si "Demander conseil" */}
                {formData.recurrence_type !== 'ask_advice' && (
                  <>
                    <div className="space-y-3">
                      <Label className="text-base font-medium">Jour préféré</Label>
                      <Select
                        value={formData.preferred_day}
                        onValueChange={(v) => setFormData(prev => ({ ...prev, preferred_day: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choisissez un jour" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableDays.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Step 2: Planning (optional - for specific dates) */}
            {currentStep === 2 && (
              <div className="space-y-4">
                {/* Counter for remaining dates */}
                {formData.recurrence_type !== 'custom' && formData.start_date && formData.end_date && (
                  <div className="p-4 rounded-lg" style={{ backgroundColor: `${primaryColor}15` }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">
                          📅 Progression de la sélection
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {RECURRENCE_OPTIONS.find(o => o.value === formData.recurrence_type)?.label}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold" style={{ color: primaryColor }}>
                          {(() => {
                            // For full_day picks each date counts once (2 half-slots = 1 day)
                            const raw = formData.selected_slots.length;
                            if (formData.slot_duration !== 'full_day') return raw;
                            const uniqueDays = new Set(formData.selected_slots.map(s => new Date(s.date).toISOString().slice(0,10)));
                            return uniqueDays.size;
                          })()} / {calculateExpectedDates()}
                        </p>
                        <p className="text-xs text-gray-500">
                          {getRemainingDatesToSelect() > 0 
                            ? `${getRemainingDatesToSelect()} restant(s)` 
                            : '✓ Complet'}
                        </p>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full transition-all duration-300 rounded-full"
                        style={{ 
                          width: (() => {
                            const raw = formData.selected_slots.length;
                            const cnt = formData.slot_duration !== 'full_day' ? raw : new Set(formData.selected_slots.map(s => new Date(s.date).toISOString().slice(0,10))).size;
                            return `${Math.min(100, (cnt / Math.max(1, calculateExpectedDates())) * 100)}%`;
                          })(),
                          backgroundColor: primaryColor 
                        }}
                      />
                    </div>
                  </div>
                )}
                
                <p className="text-sm text-gray-600">
                  Sélectionnez vos créneaux souhaités
                  {formData.recurrence_type !== 'custom' && formData.start_date && formData.end_date && (
                    <span className="text-gray-400"> • Les créneaux grisés sont déjà réservés</span>
                  )}
                </p>

                {(() => {
                  // When a preferred collaborator is chosen, warn the user
                  // (inline + on next click) if any selected slot conflicts
                  // with that collab's unavailable dates (leave / busy).
                  if (!formData.preferred_collaborator_id) return null;
                  const unavailable = new Set(collabUnavailableDates[formData.preferred_collaborator_id] || []);
                  if (unavailable.size === 0) return null;
                  const conflicting = formData.selected_slots
                    .map(s => new Date(s.date).toISOString().split('T')[0])
                    .filter(d => unavailable.has(d));
                  const unique = Array.from(new Set(conflicting));
                  if (unique.length === 0) return null;
                  const collabName = companyCollaborators.find(c => c.id === formData.preferred_collaborator_id)?.name || 'Cet intervenant';
                  return (
                    <div data-testid="recurring-collab-conflict-warning" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                      <strong>{collabName}</strong> n'est pas disponible sur {unique.length} de vos dates sélectionnées
                      {' '}({unique.slice(0, 3).map(d => format(new Date(d), 'dd/MM')).join(', ')}{unique.length > 3 ? `…` : ''}).
                      Choisissez un autre intervenant ou changez ces dates.
                    </div>
                  );
                })()}
                
                <div className="flex items-center justify-between mb-4">
                  <Button variant="outline" size="icon" onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1))}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="font-medium capitalize">
                    {format(currentMonth, 'MMMM yyyy', { locale: fr })}
                  </span>
                  <Button variant="outline" size="icon" onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1))}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-7 gap-1 text-center text-sm">
                  {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(d => (
                    <div key={d} className="font-medium py-2 text-gray-500">{d}</div>
                  ))}
                  {getDaysInMonth().map((day, idx) => {
                    const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                    const isAvailable = isDateAvailable(day);
                    const closureReason = getClosureReason(day);
                    const morningSelected = isSlotSelected(day, 'morning');
                    const afternoonSelected = isSlotSelected(day, 'afternoon');
                    const anySelected = morningSelected || afternoonSelected;
                    
                    // Check if slots are booked
                    const morningBooked = isSlotBooked(day, 'morning');
                    const afternoonBooked = isSlotBooked(day, 'afternoon');

                    // When a preferred collab is selected and unavailable on
                    // this specific date (leave / busy day), flag the whole
                    // cell so the user sees WHY the day is greyed out.
                    const dateStr = format(day, 'yyyy-MM-dd');
                    const preferredCollabUnavailable = (
                      formData.preferred_collaborator_id
                      && (collabUnavailableDates[formData.preferred_collaborator_id] || []).includes(dateStr)
                    );
                    const preferredCollabName = preferredCollabUnavailable
                      ? (companyCollaborators.find(c => c.id === formData.preferred_collaborator_id)?.name || 'Intervenant')
                      : null;
                    // For the tight day cell, keep just the first name so the
                    // label fits without CSS truncation (full name stays in the
                    // native tooltip via `title`).
                    const preferredCollabShortName = preferredCollabName
                      ? preferredCollabName.split(' ')[0]
                      : null;

                    // When the user picked a "Journée complète" slot, we
                    // must NOT offer the AM/PM toggle: a single click on the
                    // day selects the entire day (book both halves).
                    const fullDaySelected = morningSelected && afternoonSelected;
                    const dayFullyBooked = morningBooked && afternoonBooked;

                    return (
                      <div
                        key={idx}
                        className={`p-1 ${!isCurrentMonth ? 'opacity-30' : ''}`}
                        title={closureReason || (preferredCollabUnavailable ? `${preferredCollabName} indisponible` : undefined)}
                      >
                        <div
                          className={`rounded-lg p-1 ${anySelected ? '' : ''} ${closureReason ? 'bg-amber-50' : preferredCollabUnavailable ? 'bg-orange-50' : ''}`}
                          style={anySelected ? { backgroundColor: `${primaryColor}10` } : {}}
                        >
                          <div className="text-xs mb-1 flex items-center justify-center gap-1">
                            <span>{format(day, 'd')}</span>
                            {closureReason && (
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            )}
                            {!closureReason && preferredCollabUnavailable && (
                              <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                            )}
                          </div>
                          {isCurrentMonth && !isAvailable && closureReason && (
                            <div className="text-[10px] text-amber-700 truncate text-center leading-tight" title={closureReason}>
                              {closureReason}
                            </div>
                          )}
                          {isCurrentMonth && isAvailable && !closureReason && preferredCollabUnavailable && (
                            <div className="text-[10px] text-orange-700 truncate text-center leading-tight" title={`${preferredCollabName} indisponible`}>
                              {preferredCollabShortName} indispo
                            </div>
                          )}
                          {isCurrentMonth && isAvailable && (
                            _isRecurringFullDay ? (
                              <button
                                onClick={() => {
                                  if (dayFullyBooked) return;
                                  // For a full-day booking, refuse the click
                                  // when one of the halves is already taken —
                                  // otherwise the user would think they
                                  // booked the full day while only AM or PM
                                  // got added.
                                  if (morningBooked || afternoonBooked) return;
                                  // Toggle both halves together so the day acts
                                  // as a single slot for full-day bookings.
                                  if (fullDaySelected) {
                                    handleSlotToggle(day, 'morning');
                                    handleSlotToggle(day, 'afternoon');
                                  } else {
                                    if (!morningSelected) handleSlotToggle(day, 'morning');
                                    if (!afternoonSelected) handleSlotToggle(day, 'afternoon');
                                  }
                                }}
                                disabled={dayFullyBooked || morningBooked || afternoonBooked}
                                className={`w-full text-xs py-1 rounded transition-colors ${
                                  dayFullyBooked || morningBooked || afternoonBooked
                                    ? 'bg-red-100 text-red-400 cursor-not-allowed line-through'
                                    : fullDaySelected
                                      ? 'text-white'
                                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                }`}
                                style={fullDaySelected && !dayFullyBooked && !morningBooked && !afternoonBooked ? { backgroundColor: primaryColor } : {}}
                                title={
                                  dayFullyBooked
                                    ? 'Journée déjà réservée'
                                    : morningBooked || afternoonBooked
                                      ? 'Journée partiellement réservée — choisissez une autre date'
                                      : 'Journée complète'
                                }
                                data-testid={`recurring-day-full-${format(day, 'yyyy-MM-dd')}`}
                              >
                                {dayFullyBooked || morningBooked || afternoonBooked ? '—' : 'Jour'}
                              </button>
                            ) : (
                            <div className="space-y-1">
                              <button
                                onClick={() => !morningBooked && handleSlotToggle(day, 'morning')}
                                disabled={morningBooked}
                                className={`w-full text-xs py-0.5 rounded transition-colors ${
                                  morningBooked
                                    ? 'bg-red-100 text-red-400 cursor-not-allowed line-through'
                                    : morningSelected 
                                      ? 'text-white' 
                                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                }`}
                                style={morningSelected && !morningBooked ? { backgroundColor: primaryColor } : {}}
                                title={morningBooked ? 'Créneau déjà réservé' : ''}
                              >
                                {morningBooked ? '—' : 'AM'}
                              </button>
                              <button
                                onClick={() => !afternoonBooked && handleSlotToggle(day, 'afternoon')}
                                disabled={afternoonBooked}
                                className={`w-full text-xs py-0.5 rounded transition-colors ${
                                  afternoonBooked
                                    ? 'bg-red-100 text-red-400 cursor-not-allowed line-through'
                                    : afternoonSelected 
                                      ? 'text-white' 
                                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                }`}
                                style={afternoonSelected && !afternoonBooked ? { backgroundColor: primaryColor } : {}}
                                title={afternoonBooked ? 'Créneau déjà réservé' : ''}
                              >
                                {afternoonBooked ? '—' : 'PM'}
                              </button>
                            </div>
                            )
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {formData.selected_slots.length > 0 && (
                  <div className="p-4 rounded-lg" style={{ backgroundColor: `${primaryColor}10` }}>
                    {(() => {
                      // For full-day mode, regroup AM+PM of the same date into
                      // a single "Journée" badge to avoid showing two badges
                      // for a single user click.
                      const displaySlots = [];
                      if (_isRecurringFullDay) {
                        const byDate = new Map();
                        for (const s of formData.selected_slots) {
                          const key = new Date(s.date).toISOString().split('T')[0];
                          const entry = byDate.get(key) || { date: s.date, hasMorning: false, hasAfternoon: false };
                          if (s.period === 'morning') entry.hasMorning = true;
                          if (s.period === 'afternoon') entry.hasAfternoon = true;
                          byDate.set(key, entry);
                        }
                        for (const entry of byDate.values()) {
                          if (entry.hasMorning && entry.hasAfternoon) {
                            displaySlots.push({ date: entry.date, period: 'full_day' });
                          } else if (entry.hasMorning) {
                            displaySlots.push({ date: entry.date, period: 'morning' });
                          } else if (entry.hasAfternoon) {
                            displaySlots.push({ date: entry.date, period: 'afternoon' });
                          }
                        }
                      } else {
                        displaySlots.push(...formData.selected_slots);
                      }
                      return (
                        <>
                          <p className="font-medium mb-2">{displaySlots.length} créneau(x) sélectionné(s)</p>
                          <div className="flex flex-wrap gap-2">
                            {displaySlots.map((slot, idx) => {
                              const periodLabel = slot.period === 'morning'
                                ? 'Matin'
                                : slot.period === 'afternoon'
                                  ? 'Après-midi'
                                  : 'Journée';
                              const removeSlot = () => {
                                if (slot.period === 'full_day') {
                                  handleSlotToggle(new Date(slot.date), 'morning');
                                  handleSlotToggle(new Date(slot.date), 'afternoon');
                                } else {
                                  handleSlotToggle(new Date(slot.date), slot.period);
                                }
                              };
                              return (
                                <Badge key={idx} variant="secondary" data-testid={`recurring-selected-slot-${slot.date}-${slot.period}`}>
                                  {format(new Date(slot.date), 'dd/MM')} - {periodLabel}
                                  <button onClick={removeSlot} className="ml-1">
                                    <X className="w-3 h-3" />
                                  </button>
                                </Badge>
                              );
                            })}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Contact */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nom complet *</Label>
                    <Input
                      value={formData.client_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                      placeholder="Jean Dupont"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.client_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                      placeholder="jean@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Téléphone *</Label>
                    <Input
                      value={formData.client_phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_phone: e.target.value }))}
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email URSSAF (optionnel)</Label>
                    <Input
                      type="email"
                      value={formData.urssaf_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, urssaf_email: e.target.value }))}
                      placeholder="Pour l'avance immédiate"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Adresse du lieu d'intervention *</Label>
                  <AddressAutocomplete
                    value={formData.client_address}
                    onChange={(v) => setFormData(prev => ({ ...prev, client_address: v }))}
                    onSelect={(sel) => setFormData(prev => ({
                      ...prev,
                      client_address: sel.label,
                      client_coords: sel.coords || null,
                    }))}
                    placeholder="Commencez à taper votre adresse…"
                    data-testid="recurring-address-autocomplete"
                    savedAddresses={savedAddresses}
                    required
                  />
                </div>
              </div>
            )}

            {/* Step 4: Summary */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="p-4 rounded-lg bg-gray-50">
                  <h3 className="font-medium mb-3">Récapitulatif de votre demande</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Services</span>
                      <span className="text-right max-w-[60%]">
                        {formData.selected_services
                          .map(id => companyServices.find(s => s.id === id)?.name || id)
                          .filter(Boolean)
                          .join(', ') || 'Aucun service sélectionné'}
                        {formData.selected_services.includes('autre') && formData.other_service_description && (
                          <span className="block text-xs text-gray-500">+ {formData.other_service_description}</span>
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Durée par passage</span>
                      <span>{formData.slot_duration === 'full_day' ? 'Journée (7h)' : 'Demi-journée (3h30)'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Fréquence</span>
                      <span>{(() => {
                        const map = {
                          weekly: '1 fois / semaine',
                          biweekly: '1 fois toutes les 2 semaines',
                          monthly: '1 fois / mois',
                          custom: 'Personnalisé',
                          ask_advice: 'Demande de conseil',
                          once: 'Une seule fois',
                        };
                        return map[formData.recurrence_type]
                          || RECURRENCE_OPTIONS.find(r => r.value === formData.recurrence_type)?.label
                          || formData.recurrence_type
                          || '—';
                      })()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Jour préféré</span>
                      <span>{DAY_OPTIONS.find(d => d.value === formData.preferred_day)?.label || formData.preferred_day || '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Créneaux</span>
                      {(() => {
                        // 1 date pickée = 1 créneau, quel que soit half/full day.
                        // Pour full_day, selected_slots contient morning+aprem
                        // par date → on déduplique par date.
                        let pickedDates = 0;
                        if (formData.selected_dates?.length) {
                          pickedDates = formData.selected_dates.length;
                        } else if (formData.slot_duration === 'full_day') {
                          pickedDates = new Set((formData.selected_slots || []).map(s => new Date(s.date).toISOString().slice(0, 10))).size;
                        } else {
                          pickedDates = (formData.selected_slots || []).length;
                        }
                        if (pickedDates === 0) {
                          return (
                            <span className="text-amber-700 font-medium bg-amber-50 border border-amber-200 rounded-md px-2 py-0.5 text-xs" data-testid="recap-slots-empty">
                              À compléter — dates non choisies
                            </span>
                          );
                        }
                        return (
                          <span data-testid="recap-slots-count">
                            {pickedDates} date{pickedDates > 1 ? 's' : ''} sélectionnée{pickedDates > 1 ? 's' : ''}
                          </span>
                        );
                      })()}
                    </div>
                    {formData.preferred_collaborator_id && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Intervenant préféré</span>
                        <span>{companyCollaborators.find(c => c.id === formData.preferred_collaborator_id)?.name || 'Non spécifié'}</span>
                      </div>
                    )}
                  </div>
                  {/* Explicit banner when no dates were picked */}
                  {(() => {
                    let pickedDates = 0;
                    if (formData.selected_dates?.length) {
                      pickedDates = formData.selected_dates.length;
                    } else if (formData.slot_duration === 'full_day') {
                      pickedDates = new Set((formData.selected_slots || []).map(s => new Date(s.date).toISOString().slice(0, 10))).size;
                    } else {
                      pickedDates = (formData.selected_slots || []).length;
                    }
                    if (pickedDates > 0) return null;
                    return (
                      <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="recap-slots-warning">
                        <span className="mt-0.5">⏳</span>
                        <span>
                          Aucune date n'a encore été planifiée pour cet abonnement.
                          Vous pourrez la choisir plus tard depuis votre espace client,
                          ou {config?.company?.name || 'l\'entreprise'} vous les proposera.
                        </span>
                      </div>
                    );
                  })()}
                </div>

                <div className="p-4 rounded-lg" style={{ backgroundColor: `${primaryColor}10` }}>
                  <h3 className="font-medium mb-3">Estimation tarifaire</h3>
                  <div className="space-y-2">
                    {_isTaxCreditEligible ? (
                      <>
                        <div className="flex justify-between items-baseline">
                          <span className="text-sm font-medium" style={{ color: primaryColor }}>
                            Reste à votre charge / passage
                          </span>
                          <span className="text-2xl font-heading font-bold" style={{ color: primaryColor }}>
                            {(pricing.perVisit / 2).toFixed(2)}€
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          Tarif de base : <span className="font-bold text-base">{pricing.perVisit.toFixed(2)}€</span> / passage
                        </p>
                        <p className="text-[11px] italic text-gray-500">
                          (Après déduction d'avance immédiate de crédit d'impôts)
                        </p>
                      </>
                    ) : (
                      <div className="flex justify-between text-lg">
                        <span>Par passage</span>
                        <span className="font-bold">{pricing.perVisit.toFixed(2)}€</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-gray-50">
                  <h3 className="font-medium mb-3">Vos coordonnées</h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-gray-600">Nom:</span> {formData.client_name}</p>
                    <p><span className="text-gray-600">Email:</span> {formData.client_email}</p>
                    <p><span className="text-gray-600">Téléphone:</span> {formData.client_phone}</p>
                    <p><span className="text-gray-600">Adresse:</span> {formData.client_address}</p>
                  </div>
                </div>

                {/* CGV Checkbox */}
                {widgetConfig?.terms_enabled && widgetConfig?.terms_link && (
                  <div className="flex items-start gap-3 p-4 rounded-lg border" style={{ borderColor: termsAccepted ? primaryColor : '#e5e7eb' }}>
                    <input
                      type="checkbox"
                      id="terms-acceptance"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mt-1 w-4 h-4 cursor-pointer"
                      data-testid="terms-checkbox"
                    />
                    <label htmlFor="terms-acceptance" className="cursor-pointer text-sm leading-relaxed">
                      {widgetConfig.terms_text || "J'accepte les conditions générales de vente"}{' '}
                      <a 
                        href={widgetConfig.terms_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="underline font-medium"
                        style={{ color: primaryColor }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Lire les CGV
                      </a>
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between pt-4">
              {currentStep > 0 ? (
                <Button variant="outline" onClick={handlePrevious}>
                  <ChevronLeft className="w-4 h-4 mr-2" /> Retour
                </Button>
              ) : (
                <div />
              )}
              
              {currentStep < STEPS.length - 1 ? (
                <Button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  style={{ backgroundColor: primaryColor }}
                >
                  Suivant <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || (widgetConfig?.terms_enabled && widgetConfig?.terms_link && !termsAccepted)}
                  style={{ backgroundColor: primaryColor }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" /> {widgetConfig?.submit_button_text || 'Envoyer la demande'}
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer - Propulsé par ResaWork + Widget ID (hidden for Premium subscribers) */}
        <WidgetFooter widgetId={widgetId} isPremium={config?.is_premium} />
      </div>
    </div>
  );
}
